<template lang="pug">
//- Reusable alt-text field with one-click AI generation. Encapsulates the
//- client-side vision call + per-image generation limit so any image editor
//- (product drawer, CMS content, business cards) shares one implementation.
//- The UI says "KI"/"AI" only — the orchestration vendor is never surfaced.
.alt-text-feature
    .alt-text-feature__header
        .alt-text-feature__label
            i.el-icon-fa-magic.fas
            span {{ $t('Alternate text for IMG tags') }}
        .alt-text-feature__meta
            el-tooltip(
                v-if="mistralEnabled"
                :disabled="!altLastTokens"
                :content="altLastTokens ? `Input: ${altLastTokens.input} · Output: ${altLastTokens.output} Tokens` : ''"
                placement="top"
                effect="dark"
                :append-to-body="true"
            )
                span
                    el-tag.alt-text-feature__badge(size="mini" effect="dark") {{ $t('AI') }}
            el-popover(placement="top" width="250" trigger="hover")
                span {{ $t('alt_hint') }}
                el-button.alt-text-feature__help(slot="reference" circle size="mini") ?
    el-input.alt-text-feature__input(
        :value="value"
        :placeholder="(image && image.description) || $t('alt_placeholder')"
        maxlength="255"
        show-word-limit
        @input="$emit('input', $event)"
    )
    .alt-text-feature__footer(v-if="mistralEnabled")
        el-tooltip(
            v-if="altGenerateCount >= altMaxGenerations"
            :content="$t('alt_limit_hint')"
            placement="top"
            effect="dark"
        )
            el-button.alt-text-feature__btn(
                size="small"
                disabled
                icon="el-icon-fa-magic fas"
            ) {{ value ? $t('Regenerate alt text') : $t('Generate alt text') }}
        el-button.alt-text-feature__btn(
            v-else
            size="small"
            :loading="altGenerating"
            :disabled="!hasImage"
            icon="el-icon-fa-magic fas"
            @click="regenerate"
        ) {{ value ? $t('Regenerate alt text') : $t('Generate alt text') }}
        p.alt-text-feature__hint(v-if="altGenerateCount > 0 && altGenerateCount < altMaxGenerations") {{ $tc('alt_remaining_hint', altMaxGenerations - altGenerateCount) }}
        p.alt-text-feature__hint {{ $t('ai_alt_hint') }}
</template>

<i18n>
{
    "de": {
        "Alternate text for IMG tags": "Bildbeschreibung (Alt-Text)",
        "alt_hint": "Der Alternativtext dient der Barrierefreiheit, indem er den Bildinhalt für Screenreader beschreibt und dem Nutzer eine textliche Entsprechung bietet, falls das Bild nicht geladen werden kann.",
        "Generate alt text": "Alt-Text generieren",
        "Regenerate alt text": "Alt-Text neu generieren",
        "ai_alt_hint": "KI-generiert · Bitte vor Veröffentlichung prüfen.",
        "alt_placeholder": "Kurze Bildbeschreibung für Screenreader...",
        "alt_limit_hint": "Limit erreicht · Bitte den Text manuell anpassen.",
        "alt_remaining_hint": "Noch 1 Generierung verfügbar | Noch {count} Generierungen verfügbar",
        "alt_limit_reached_notify": "Limit erreicht – Alt-Text bitte manuell prüfen.",
        "Alt text could not be generated.": "Alt-Text konnte nicht generiert werden.",
        "alt_prompt": "Erstelle einen prägnanten, beschreibenden Alt-Text für dieses Bild auf Deutsch. Maximal 125 Zeichen. Nur den Alt-Text, keine weiteren Erklärungen.",
        "AI": "KI"
    },
    "en": {
        "alt_hint": "The alternative text supports accessibility by describing the image content for screen readers and providing a textual equivalent for users when the image cannot be loaded.",
        "ai_alt_hint": "AI-generated · Please review before publishing.",
        "alt_placeholder": "Short image description for screen readers...",
        "alt_limit_hint": "Limit reached · Please adjust the text manually.",
        "alt_remaining_hint": "1 generation left | {count} generations left",
        "alt_limit_reached_notify": "Limit reached – please review the alt text manually.",
        "alt_prompt": "Write a concise, descriptive alt text for this image in English. Maximum 125 characters. Return only the alt text, with no further explanation."
    }
}
</i18n>

<script>
import _ from 'lodash';
import axios from 'axios';
import { originalImageUrl } from '@/extensions/generative-media/imageThumbnail';
import { useSetupStore } from '@/stores';

export default {
    name: 'AltTextField',
    props: {
        // The alt text (v-model).
        value: { type: String, default: '' },
        // The image to describe — needs a resolvable URL and a stable id for the
        // per-image generation counter. `description` seeds the placeholder.
        image: { type: Object, default: null },
    },
    data() {
        return {
            altGenerating: false,
            altGenerateCount: 0,
            altLastTokens: null,
        };
    },
    computed: {
        mistralEnabled() {
            return !!process.env.VUE_APP_MISTRAL_ALT_API_KEY
                && process.env.VUE_APP_MISTRAL_ALT_ENABLED !== 'false';
        },
        altMaxGenerations() {
            return parseInt(process.env.VUE_APP_MISTRAL_ALT_MAX_ATTEMPTS || '5', 10);
        },
        imageId() {
            return _.get(this.image, 'id', null);
        },
        hasImage() {
            return !!this.resolveImageUrl();
        },
        // Auto-generation is opt-out via CMS settings (mirrors the content editor):
        // altAutoGenerate enables the feature, altAutoTrigger fires it on open.
        altAutoTriggered() {
            const settings = useSetupStore().settingValues || {};
            return _.get(settings, 'altAutoGenerate', true) !== false
                && _.get(settings, 'altAutoTrigger', true) !== false;
        },
    },
    watch: {
        // Re-read the counter when the editor switches to a different image, and
        // auto-generate once for a fresh image that has no alt yet (count 0 = never
        // generated, so reopening an image we already tried won't re-spend credits).
        imageId: {
            immediate: true,
            handler(id) {
                this.altGenerateCount = this.loadAltGenerateCount(id);
                this.altLastTokens = null;
                if (this.mistralEnabled && this.altAutoTriggered && this.hasImage
                    && !this.value && this.altGenerateCount === 0) {
                    this.$nextTick(() => this.regenerate());
                }
            },
        },
    },
    methods: {
        resolveImageUrl() {
            // Fresh uploads have no persisted `source.url` — fall back to the
            // uploads-table route so alt generation works before the image is saved.
            return _.get(this.image, 'url') || _.get(this.image, 'source.url') || originalImageUrl(this.image) || '';
        },
        loadAltGenerateCount(imageId) {
            if (!imageId) return 0;
            return parseInt(localStorage.getItem(`mistral:alt:${imageId}`) || '0', 10);
        },
        saveAltGenerateCount(imageId, count) {
            if (!imageId) return;
            localStorage.setItem(`mistral:alt:${imageId}`, String(count));
        },
        // Downscale the image client-side before sending it to the vision model —
        // keeps the request small and cheap.
        async getImageAsDataUrl(imageUrl, maxWidth) {
            const response = await axios.get(imageUrl, { responseType: 'blob' });
            return new Promise((resolve, reject) => {
                const objectUrl = URL.createObjectURL(response.data);
                const img = new Image();
                img.onload = () => {
                    URL.revokeObjectURL(objectUrl);
                    const canvas = document.createElement('canvas');
                    let width = img.naturalWidth;
                    let height = img.naturalHeight;
                    if (maxWidth && width > maxWidth) {
                        height = Math.round(height * maxWidth / width);
                        width = maxWidth;
                    }
                    canvas.width = width;
                    canvas.height = height;
                    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', 0.85));
                };
                img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('load failed')); };
                img.src = objectUrl;
            });
        },
        async generateAltText(width = parseInt(process.env.VUE_APP_MISTRAL_ALT_WIDTH_IMAGE_INITIAL || '512', 10)) {
            const apiKey = process.env.VUE_APP_MISTRAL_ALT_API_KEY;
            const rawUrl = this.resolveImageUrl();
            if (!apiKey || !rawUrl) return '';

            let imageData = rawUrl;
            try {
                imageData = await this.getImageAsDataUrl(rawUrl, width);
            } catch (error) {
                // Fall back to the direct URL when the canvas pre-scale fails.
            }

            const { data } = await axios.post(
                process.env.VUE_APP_MISTRAL_ALT_API_URL || 'https://api.mistral.ai/v1/chat/completions',
                {
                    model: process.env.VUE_APP_MISTRAL_ALT_MODEL || 'pixtral-12b-2409',
                    messages: [{
                        role: 'user',
                        content: [
                            { type: 'image_url', image_url: imageData },
                            // Locale-aware: the prompt (and so the generated alt text) follows
                            // the admin UI language via the <i18n> block, not a hardcoded one.
                            { type: 'text', text: this.$t('alt_prompt') },
                        ],
                    }],
                },
                { headers: { Authorization: `Bearer ${apiKey}`, 'Content-Type': 'application/json' } },
            );

            this.altLastTokens = {
                input: _.get(data, 'usage.prompt_tokens', '?'),
                output: _.get(data, 'usage.completion_tokens', '?'),
            };
            return _.get(data, 'choices[0].message.content', '').trim();
        },
        async regenerate() {
            if (!this.hasImage || this.altGenerating) return;
            this.altGenerating = true;
            this.altGenerateCount += 1;
            this.saveAltGenerateCount(this.imageId, this.altGenerateCount);
            try {
                const retryWidth = this.altGenerateCount <= 1
                    ? parseInt(process.env.VUE_APP_MISTRAL_ALT_WIDTH_IMAGE_RETRY || '512', 10)
                    : parseInt(process.env.VUE_APP_MISTRAL_ALT_WIDTH_IMAGE_RETRY_MAX || '700', 10);
                const alt = await this.generateAltText(retryWidth);
                if (alt) this.$emit('input', alt);
                if (this.altGenerateCount >= this.altMaxGenerations) {
                    this.$notify({ title: this.$t('alt_limit_reached_notify'), type: 'warning', position: 'bottom-left', customClass: 'dark', duration: 5000 });
                }
            } catch (error) {
                this.altGenerateCount -= 1;
                this.saveAltGenerateCount(this.imageId, this.altGenerateCount);
                this.$notify.error({ title: this.$t('Alt text could not be generated.'), position: 'bottom-left', customClass: 'dark', duration: 5000 });
            } finally {
                this.altGenerating = false;
            }
        },
    },
};
</script>

<style lang="sass" scoped>
.alt-text-feature
    border: 1px solid #ddd6fe
    border-radius: 10px
    background: linear-gradient(140deg, #faf8ff 0%, #eff0ff 100%)
    padding: 12px 14px
    width: 100%

    &__header
        display: flex
        align-items: center
        justify-content: space-between
        margin-bottom: 10px

    &__label
        display: flex
        align-items: center
        gap: 6px
        font-size: 12px
        font-weight: 600
        color: #5b4de8
        i
            font-size: 11px
            margin-right: 4px

    &__meta
        display: flex
        align-items: center
        gap: 6px

    &__badge
        font-size: 10px
        font-weight: 700
        letter-spacing: 0.5px
        background: #5b4de8 !important
        border-color: #5b4de8 !important
        color: #fff !important
        border-radius: 4px
        padding: 0 5px

    &__help
        padding: 0
        height: 20px
        width: 20px
        min-height: auto
        min-width: auto !important
        font-size: 11px
        font-weight: 600
        background: #ede9fe
        border-color: #c4b5fd
        color: #5b4de8

    &__input
        &::v-deep .el-input__inner
            border-color: #c4b5fd
            border-radius: 6px
            background: #fff
            &:focus
                border-color: #5b4de8
                box-shadow: 0 0 0 2px rgba(91, 77, 232, 0.1)

    &__footer
        margin-top: 10px
        display: flex
        flex-direction: column
        gap: 6px

    &__btn
        align-self: flex-start
        background: #5b4de8
        border-color: #5b4de8
        color: #fff
        font-weight: 600
        border-radius: 6px
        &:hover, &:focus
            background: #4c3dd3
            border-color: #4c3dd3
            color: #fff

    &__hint
        margin: 0
        font-size: 11px
        color: #9ca3af
        line-height: 1.4
</style>
